/**
 * identity-service (:3001) public routes, relative to the centralized service URL config
 * (which already includes `/api/v1`).
 *
 * Source of truth: `hexaunity-backend/apps/identity-service/src/modules/**`
 * controllers, listed in `postman/API_ENDPOINT_INVENTORY.md`. Validated by
 * `scripts/validate-api-contract.mjs`.
 *
 * identity-service declares no application of its own; only the routes marked
 * `@ForApplications(FINANCE)` (role writes, PUT applications, PUT companies,
 * PUT finance-access) require the FINANCE application. The Finance web app
 * administers access through finance-service `/access/*` instead
 * (ACCESS-ADMINISTRATION.md), so those identity write routes are deliberately
 * not used by any frontend.
 */
export const IDENTITY = {
  auth: {
    login: '/auth/login',
    logout: '/auth/logout',
    logoutAll: '/auth/logout-all',
    refresh: '/auth/refresh',
    me: '/auth/me',
    changePassword: '/auth/change-password',
  },
  /** Login accounts. Created only through `provisioning`; retired with `is_active: false`. */
  users: {
    list: '/users',
    user: (userId: string) => `/users/${userId}`,
    roles: (userId: string) => `/users/${userId}/roles`,
    companies: (userId: string) => `/users/${userId}/companies`,
    applications: (userId: string) => `/users/${userId}/applications`,
  },
  roles: {
    list: '/roles',
    /** Roles the CALLER may assign (seniority-filtered) — fills every role picker. */
    assignable: '/roles/assignable',
    role: (roleId: string) => `/roles/${roleId}`,
    permissions: (roleId: string) => `/roles/${roleId}/permissions`,
  },
  /** The permission catalogue (`meta` groups it by module). */
  permissions: '/permissions',
  employees: {
    /** Roles, role-derived, employee-specific and effective permissions. */
    permissions: (employeeId: string) => `/employees/${employeeId}/permissions`,
    financeAccess: (employeeId: string) => `/employees/${employeeId}/finance-access`,
    financeAccessHistory: (employeeId: string) => `/employees/${employeeId}/finance-access/history`,
    /** Administrator reset; returns a temporary password once. */
    resetPassword: (employeeId: string) => `/employees/${employeeId}/reset-password`,
  },
  provisioning: {
    /** Employee + login account in one saga (People employee, Identity user). */
    employees: '/provisioning/employees',
    /** Login account for an existing employee. */
    employeeAccount: (employeeId: string) => `/provisioning/employees/${employeeId}/account`,
    /** Login account with no employment record. */
    users: '/provisioning/users',
    /** Create the employment record for an account that has none. */
    userEmployee: (userId: string) => `/provisioning/users/${userId}/employee`,
    /** Link an account to an employee that already exists. */
    linkUserEmployee: (userId: string, employeeId: string) =>
      `/provisioning/users/${userId}/employee/${employeeId}`,
  },
} as const;
